import { Box } from "@outschool/backpack";
import { useTranslation } from "@outschool/localization";
import {
  calculateDateBounds,
  dayjs,
  guessBrowserTimeZone
} from "@outschool/time";
import { gql, useQueryWithPreviousData } from "@outschool/ui-apollo";
import { Checkbox, Loading } from "@outschool/ui-legacy-component-library";
import { assoc, not, prop } from "ramda";
import React from "react";
import { ViewsProps } from "react-big-calendar";

import {
  TeacherPublicScheduleCalendarQuery,
  TeacherPublicScheduleCalendarQueryVariables
} from "../../../shared/generated/graphql";
import * as Section from "../../../shared/Section";
import { CalendarEvent, CalendarMeeting } from "../CalendarTypes";
import ScheduleCalendar, { DateSelector } from "../ScheduleCalendar";

export function meetingToEvent({
  meeting,
  allDay = false
}: $TSFixMe): CalendarEvent {
  const { uid, section, start_time, end_time } = meeting;

  return {
    id: uid,
    title: section.activity.title,
    section,
    start: new Date(start_time),
    end: new Date(end_time),
    allDay,
    meeting
  };
}

TeacherPublicScheduleCalendar.fragments = {
  meeting: gql`
    fragment TeacherPublicScheduleCalendar_meeting on Meeting {
      uid
      start_time
      end_time
      sectionSequenceNumber
      section {
        uid
        size_min
        size_max
        filledSpaceCount
        deleted_at
        canceled_at
        published_at
        start_time
        end_time
        ongoing_stopped_at
        meetingCount
        isBuyable
        nextOngoingMeeting {
          uid
          start_time
        }

        activity {
          uid
          slug_id
          title
          titleTranslated
          weekly_meetings
          is_ongoing_weekly
          duration_weeks
          duration_minutes
        }
      }
    }
  `,
  section: gql`
    fragment TeacherPublicScheduleCalendar_section on Section {
      uid
      start_time
      end_time
      size_min
      size_max
      filledSpaceCount
      ongoing_stopped_at
      deleted_at
      canceled_at
      published_at
      isBuyable

      activity {
        uid
        slug_id
        title
        titleTranslated
        weekly_meetings
        is_ongoing_weekly
        duration_weeks
        duration_minutes
      }
    }
  `
};

const teacherPublicScheduleCalendarQuery = gql`
  query TeacherPublicScheduleCalendar(
    $uid: ID
    $startAfter: DateTime!
    $startBefore: DateTime!
  ) {
    publicProfile: publicProfileNew(uid: $uid) {
      uid
      leader {
        uid
        flexSections(startAfter: $startAfter, startBefore: $startBefore) {
          edges {
            node {
              ...TeacherPublicScheduleCalendar_section
            }
          }
        }
        meetings(startAfter: $startAfter, startBefore: $startBefore) {
          edges {
            node {
              ...TeacherPublicScheduleCalendar_meeting
            }
          }
        }
      }
    }
  }

  ${TeacherPublicScheduleCalendar.fragments.meeting}
  ${TeacherPublicScheduleCalendar.fragments.section}
`;

export default function TeacherPublicScheduleCalendar({
  views,
  view,
  onView,
  canParentEnroll,
  leaderUid,
  userTimeZone
}: {
  views?: ViewsProps;
  view?: string;
  onView?: (view: string) => void;
  canParentEnroll: boolean;
  leaderUid: string;
  userTimeZone: string;
}) {
  const [date, setUpcomingCalendarDate] = React.useState(() => new Date());
  const { t } = useTranslation(
    "client\\components\\teacher\\TeacherPublicScheduleCalendar"
  );

  view = view ?? t("week");

  const { start, end } = calculateDateBounds(date, view, userTimeZone);
  const { loading, data } = useQueryWithPreviousData<
    TeacherPublicScheduleCalendarQuery,
    TeacherPublicScheduleCalendarQueryVariables
  >(teacherPublicScheduleCalendarQuery, {
    variables: {
      uid: leaderUid,
      startAfter: start,
      startBefore: end
    }
  });

  const { leader } = data?.publicProfile ?? {};
  const { meetings: meetingConnection, flexSections: flexSectionsConnection } =
    leader ?? {};

  // eslint-disable-next-line i18next/no-literal-string
  const [meetings, flexSections] = [
    meetingConnection?.edges?.map(({ node }) => node) ?? [],
    flexSectionsConnection?.edges?.map(prop("node")) ?? []
  ];

  const filtersInitialState = {
    includeFlex: false,
    onlyEnrollable: true
  };

  const reducer = (
    state: typeof filtersInitialState,
    action: { name: keyof typeof filtersInitialState }
  ) => assoc(action.name, not(prop(action.name, state)), state);
  // Reducer used to handle multiple boolean switches.
  // It uses Ramda's assoc which will "associate" a new value to the key "name" returning
  // a new copy of the object.

  const [filters, switchFilter] = React.useReducer(
    reducer,
    filtersInitialState
  );

  // eslint-disable-next-line i18next/no-literal-string
  const scheduledEvents = meetings
    .filter(meeting => !Section.isPrivate(meeting.section))
    .filter(meeting =>
      filters.onlyEnrollable
        ? meeting.section.isBuyable &&
          canParentEnroll &&
          (!meeting.section.activity.is_ongoing_weekly ||
            (!!meeting.section.nextOngoingMeeting &&
              dayjs(meeting.start_time).isSame(
                meeting.section.nextOngoingMeeting.start_time,
                "week"
              ) &&
              dayjs(meeting.start_time).isAfter()))
        : true
    )
    .map(
      (meeting: CalendarMeeting): CalendarEvent => meetingToEvent({ meeting })
    );

  const flexEvents = filters.includeFlex
    ? flexSections
        .filter(section => !Section.isPrivate(section))
        .filter(section =>
          filters.onlyEnrollable ? section.isBuyable && canParentEnroll : true
        )
        .map((section): CalendarEvent => {
          const { uid, start_time, end_time } = section;

          return meetingToEvent({
            meeting: {
              uid,
              start_time,
              end_time,
              section
            },
            allDay: true
          });
        })
    : [];

  const events = [...scheduledEvents, ...flexEvents];

  // The transorm rotate 0 is used so that the loading indicator stays in the center of the calendar.
  // Without a transform, the spinner will stick to the center of the viewport.
  // https://developer.mozilla.org/en-US/docs/Web/CSS/position#fixed
  return (
    <Box
      data-test-id="teacher-public-schedule"
      sx={{
        transform: "rotate(0deg)"
      }}
    >
      {loading && (
        <Box
          sx={{
            zIndex: 1005,
            position: "fixed",
            top: "50%",
            left: "50%"
          }}
        >
          <Loading delay={0} />
        </Box>
      )}
      <ScheduleCalendar
        views={views}
        view={view}
        onView={onView}
        date={date}
        onNavigate={setUpcomingCalendarDate}
        events={events}
        loading={loading}
        Toolbar={props => (
          <Toolbar filters={filters} switchFilter={switchFilter} {...props} />
        )}
      />
    </Box>
  );
}

function Toolbar({
  view,
  label,
  onNavigate,
  localizer,
  date,
  filters,
  switchFilter
}: $TSFixMe) {
  // Guessing the browser timezone because react big calendar will always position events according to the browser time.
  // If the userTimeZone is PDT, but the browser is on EST, the event will be position with EST as Timezone.
  //
  // Now that there's a popover displayed on mouseover which displays the time of the meeting this problem is now
  // more relevant.
  //
  // I've decided to preserve all times in the browser timezone because I wasn't able to position them on the userTimeZone.
  const browserTz = guessBrowserTimeZone();
  const { t } = useTranslation(
    "client\\components\\teacher\\TeacherPublicScheduleCalendar"
  );

  return (
    <Box
      flex
      sx={{
        flexDirection: "column",
        gap: "15px"
      }}
    >
      <Box
        sx={{
          color: "gray"
        }}
      >
        {t("All times shown in {{browserTz}}", { browserTz })}
      </Box>
      <Box
        flex
        sx={{
          justifyContent: "space-between",
          marginBottom: "1em"
        }}
      >
        <DateSelector
          view={view}
          date={date}
          label={label}
          userTimeZone={browserTz}
          localizer={localizer}
          onNavigate={onNavigate}
        />

        <Box
          flex
          sx={{
            flexDirection: "column",
            gap: "10px"
          }}
        >
          <Checkbox
            name="canEnroll"
            value="canEnroll"
            checked={filters.onlyEnrollable}
            label={t("Show upcoming times I can enroll in")}
            onChange={() => {
              // eslint-disable-next-line i18next/no-literal-string
              switchFilter({ name: "onlyEnrollable" });
            }}
          />
          <Checkbox
            name="includeFlex"
            value="includeFlex"
            checked={filters.includeFlex}
            label={t("Show Flexible Schedule classes")}
            onChange={() => {
              // eslint-disable-next-line i18next/no-literal-string
              switchFilter({ name: "includeFlex" });
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}
